import {
	TableItemsOptions,
	TEMPLATE_ROLE_DUMMY_ID,
	UserPermission,
	UserPermissionItem,
	UserPermissionItemGroup,
	UserRoleDto,
	ItemWithOwnerCenter,
} from './../../types/index';
import { Module } from 'vuex';
import { EditingUser, RootState, UserState } from '../types';
import { api } from '../../plugins/salut-api';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';

const SHOW_LOADING = 'SHOW_LOADING';
const HIDE_LOADING = 'HIDE_LOADING';
const SET_LAST_ERROR = 'SET_LAST_ERROR';
const SET_PAGE_DATA = 'SET_PAGE_DATA';
const SET_USERS = 'SET_USERS';
const SET_USERS_WITH_DELETED = 'SET_USERS_WITH_DELETED';
const SET_EDITING_USER = 'SET_EDITING_USER';
const SET_EDITING_USER_PRIVATE_ROLE_ID = 'SET_EDITING_USER_PRIVATE_ROLE_ID';
const SET_USER_ROLE_TEMPLATES = 'SET_USER_ROLE_TEMPLATES';
const SET_USER_ROLES = 'SET_USER_ROLES';
const SET_EDITING_USER_ROLE = 'SET_EDITING_USER_ROLE';
const SET_USER_PERMISSIONS = 'SET_USER_PERMISSIONS';
const SET_PERMISSIONS_EDITING = 'SET_PERMISSIONS_EDITING';
const RESET_STATE = 'RESET_STATE';

export const UserActionKeys = {
	fetchUsers: 'fetchUsers',
	fetchUsersWithDeleted: 'fetchUsersWithDeleted',
	fetchUsersForTable: 'fetchUsersForTable',
	fetchUserForEdit: 'fetchUserForEdit',
	fetchRoleTemplates: 'fetchRoleTemplates',
	fetchRoles: 'fetchRoles',
	unsetRoles: 'unsetRoles',
	fetchUserPermissions: 'fetchUserPermissions',
	setEditingUserRole: 'setEditingUserRole',
	unsetEditingUserRole: 'unsetEditingUserRole',
	saveEditingRole: 'saveEditingRole',
	initEditingUser: 'initEditingUser',
	setEditingUser: 'setEditingUser',
	unsetEditingUser: 'unsetEditingUser',
	saveEditingUser: 'saveEditingUser',
	setEditingUserRoleFromEditingUser: 'setEditingUserRoleFromEditingUser',
	setPermissionsEditing: 'setPermissionsEditing',
	unsetPermissionsEditing: 'unsetPermissionsEditing',
	fetchAvailableUsers: 'fetchAvailableUsers',
	resetState: 'resetState',
};

export const UserGetterKeys = {
	roleTemplates: 'roleTemplates',
	roles: 'roles',
	customRoles: 'customRoles',
	editingRole: 'editingRole',
	userPermissionItems: 'userPermissionItems',
	userPermissions: 'userPermissions',
	userPermissionItemGroups: 'userPermissionItemGroups',
	editingUser: 'editingUser',
	editingUserPrivateRoleId: 'editingUserPrivateRoleId',
	permissionsEditing: 'permissionsEditing',
	originalEditingRole: 'originalEditingRole',
	users: 'users',
	usersWithDeleted: 'usersWithDeleted',
};

const initialState: UserState = {
	pageData: { items: [], total: 0 },
	loading: 0,
	lastError: '',
	users: [],
	usersWithDeleted: [],
	editingUser: undefined,
	editingUserPrivateRoleId: undefined,
	userTemplates: [],
	userRoles: undefined,
	editingUserRole: null,
	userPermissions: [],
	permissionsEditing: false,
};

const store: Module<UserState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	mutations: {
		[SHOW_LOADING](state) {
			state.loading += 1;
		},

		[HIDE_LOADING](state) {
			state.loading -= 1;
		},

		[SET_LAST_ERROR](state, error) {
			state.lastError = error;
		},

		[SET_PAGE_DATA](state, { result, total }) {
			state.pageData = {
				items: result,
				total: total,
			};
		},

		[SET_USERS](state, data) {
			state.users = data;
		},

		[SET_USERS_WITH_DELETED](state, data) {
			state.usersWithDeleted = data;
		},

		[SET_EDITING_USER](state, user: EditingUser) {
			state.editingUser = user;
		},

		[SET_EDITING_USER_PRIVATE_ROLE_ID](state, roleId?: string) {
			state.editingUserPrivateRoleId = roleId;
		},

		[SET_USER_ROLE_TEMPLATES](state, templates) {
			state.userTemplates = templates;
		},

		[SET_USER_ROLES](state, roles) {
			state.userRoles = roles;
		},

		[SET_EDITING_USER_ROLE](state, role: UserRoleDto) {
			state.editingUserRole = role;
		},

		[SET_USER_PERMISSIONS](state, permissions: UserPermission[]) {
			state.userPermissions = permissions;
		},

		[SET_PERMISSIONS_EDITING](state, isEditing: boolean) {
			state.permissionsEditing = isEditing;
		},

		[RESET_STATE](state) {
			Object.assign(state, cloneDeep(initialState));
		},
	},

	actions: {
		async [UserActionKeys.fetchUsers](
			{ commit },
			payload?: {
				ngoId?: number;
				centerIds?: number[];
				includeAccessible?: boolean;
			},
		) {
			const users = await api.fetchUsers(payload || {});
			commit(SET_USERS, users);
		},

		async [UserActionKeys.fetchUsersWithDeleted](
			{ commit },
			payload?: {
				ngoId?: number;
				centerIds?: number[];
				includeAccessible?: boolean;
			},
		) {
			const users = await api.fetchUsers({
				...(payload || {}),
				withDeleted: true,
			});
			commit(SET_USERS_WITH_DELETED, users);
		},

		async [UserActionKeys.fetchUsersForTable](
			{ commit },
			options: TableItemsOptions,
		) {
			try {
				commit(SHOW_LOADING);

				const data = await api.fetchUserItems(options);

				commit(SET_PAGE_DATA, data);
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [UserActionKeys.fetchUserForEdit](
			{ commit },
			{ userId }: { userId: string },
		) {
			// clear private role ID
			commit(SET_EDITING_USER_PRIVATE_ROLE_ID, undefined);

			const user = await api.fetchUser(userId);
			const userDto: EditingUser = {
				id: user.id,
				name: user.name,
				jobTitle: user.jobTitle,
				email: user.email,
				phone: user.phone,
				ngoId: user.ngoId,
				centerId: user.centerId,
				roleId: user.role?.id,
				accessCenterIds: user.accessCenters?.map(ac => ac.centerId) || [],
			};
			if (user.role?.isPrivate) {
				userDto.privateRole = {
					name: user.role.name,
					permissionIds: user.role.permissions.map(p => p.id),
					baseRoleId: user.role.baseRoleId,
				};
				// set private role ID
				commit(SET_EDITING_USER_PRIVATE_ROLE_ID, user.role.id);
			}
			commit(SET_EDITING_USER, userDto);
		},

		async [UserActionKeys.fetchRoleTemplates]({ commit, state }) {
			if (state.userTemplates.length)
				// data is static, thus no need to reload
				return;
			const templates = await api.fetchUserRoleTemplates();
			commit(SET_USER_ROLE_TEMPLATES, templates);
		},

		async [UserActionKeys.fetchRoles]({ commit }) {
			const roles = await api.fetchUserRoles();
			commit(SET_USER_ROLES, roles);
		},

		async [UserActionKeys.unsetRoles]({ commit }) {
			commit(SET_USER_ROLES, undefined);
		},

		[UserActionKeys.setEditingUserRole](
			{ commit },
			{ role }: { role: UserRoleDto },
		) {
			commit(SET_EDITING_USER_ROLE, role);
		},

		[UserActionKeys.unsetEditingUserRole]({ commit }) {
			commit(SET_EDITING_USER_ROLE, null);
		},

		async [UserActionKeys.fetchUserPermissions]({ commit, state }) {
			if (state.userPermissions.length)
				// data is static, thus no need to reload
				return;
			const permissionItems = await api.fetchUserPermissions();
			commit(SET_USER_PERMISSIONS, permissionItems);
		},

		async [UserActionKeys.saveEditingRole]({ getters, dispatch }) {
			const { id, ...roleDto } = getters[UserGetterKeys.editingRole];
			if (!id || id === TEMPLATE_ROLE_DUMMY_ID) {
				// from template
				await api.createUserRole(roleDto);
			} else {
				await api.updateUserRole(id, roleDto);
			}
			return dispatch(UserActionKeys.fetchRoles);
		},

		[UserActionKeys.setEditingUser](
			{ commit },
			{ user }: { user: EditingUser },
		) {
			commit(SET_EDITING_USER, user);
		},

		[UserActionKeys.unsetEditingUser]({ commit }) {
			commit(SET_EDITING_USER, null);
		},

		[UserActionKeys.initEditingUser]({ commit }) {
			const userDto: EditingUser = {
				name: '',
				jobTitle: '',
				ngoId: 0,
				centerId: 0,
				phone: '',
				email: '',
				roleId: undefined,
				accessCenterIds: [],
			};

			commit(SET_EDITING_USER, userDto);
		},

		async [UserActionKeys.saveEditingUser]({ getters }) {
			const editingUser: EditingUser = getters[UserGetterKeys.editingUser];
			if (!editingUser) throw 'Missing editingUser';
			const { name, ngoId, email, centerId, roleId, privateRole } = editingUser;
			if (editingUser.id) {
				// update user
				await api.updateUser(editingUser.id, {
					// need to exclude email coz it's not changable at this stage
					// transform role obj to roleId to match with Dto
					...omit(editingUser, ['email', 'id']),
					name: name || '',
					ngoId: ngoId as number,
					centerId: centerId as number,
					privateRole,
					roleId: privateRole ? undefined : roleId,
				});
			} else {
				// create user
				await api.createUser({
					...editingUser,
					name: name || '',
					email: email || '',
					ngoId: ngoId as number,
					centerId: centerId as number,
					privateRole,
					roleId: privateRole ? undefined : roleId,
				});
			}
		},

		[UserActionKeys.setEditingUserRoleFromEditingUser]({ state, dispatch }) {
			if (!state.editingUser) {
				throw 'Missing editingUser';
			}
			const role = state.userRoles?.find(
				role => role.id === state.editingUser?.roleId,
			);
			if (!role) throw `Cannot find role with ID#${state.editingUser.roleId}`;
			dispatch(UserActionKeys.setEditingUserRole, {
				role: {
					id: role.id,
					name: role.name,
					code: role.code,
					permissionIds: role.permissions.map(p => p.id),
					baseRoleId: role.baseRoleId,
					ngoId: role.ngoId as number,
				},
			});
		},

		[UserActionKeys.setPermissionsEditing]({ commit }) {
			commit(SET_PERMISSIONS_EDITING, true);
		},

		[UserActionKeys.unsetPermissionsEditing]({ commit }) {
			commit(SET_PERMISSIONS_EDITING, false);
		},

		[UserActionKeys.fetchAvailableUsers](
			{ state, dispatch },
			{
				ngoId,
				itemWithOwner,
			}: { ngoId: number; itemWithOwner: ItemWithOwnerCenter },
		) {
			if (!itemWithOwner) {
				// should not happen, just skip
				return;
			}
			// ATTENTION: possible privilege escalation bug, if current user is has no access to some of the centers
			// TODO: patch this
			if (itemWithOwner.collaboratorAllCenters) {
				// return staff of the whole NGO
				return dispatch(
					`user/${UserActionKeys.fetchUsers}`,
					{
						ngoId,
					},
					{ root: true },
				);
			} else if (itemWithOwner.collaboratorCenterIds?.length) {
				// return staff of these centers
				const centerIds = [
					...itemWithOwner.collaboratorCenterIds,
					itemWithOwner.ownerCenterId,
				];
				return dispatch(
					`user/${UserActionKeys.fetchUsers}`,
					{ centerIds },
					{ root: true },
				);
			} else {
				// just get the staff list of the owner center
				return dispatch(
					`user/${UserActionKeys.fetchUsers}`,
					{ centerIds: [itemWithOwner.ownerCenterId] },
					{ root: true },
				);
			}
		},

		async [UserActionKeys.resetState]({ commit }) {
			commit(RESET_STATE);
		},
	},

	getters: {
		loading: state => Boolean(state.loading),
		lastError: state => state.lastError,
		[UserGetterKeys.users]: state => state.users,
		[UserGetterKeys.usersWithDeleted]: state => state.usersWithDeleted,
		[UserGetterKeys.editingUser]: state => state.editingUser,
		[UserGetterKeys.editingUserPrivateRoleId]: state =>
			state.editingUserPrivateRoleId,
		pageData: state => state.pageData,
		[UserGetterKeys.roleTemplates]: state => state.userTemplates || [],
		[UserGetterKeys.roles]: state => state.userRoles,
		[UserGetterKeys.customRoles]:
			// only roles that are editable (owned) by NGO
			state => state.userRoles?.filter(role => role.ngoId && !role.isPrivate),
		[UserGetterKeys.editingRole]: state => state.editingUserRole,
		[UserGetterKeys.originalEditingRole]: state => {
			if (!state.editingUserRole?.id) return null;
			const originalRole = state.userRoles?.find(
				role => role.id == state.editingUserRole?.id,
			);
			if (!originalRole) return null;
			if (!originalRole.ngoId) return null;
			const editingRole: UserRoleDto = {
				id: originalRole.id,
				ngoId: originalRole.ngoId,
				name: originalRole.name,
				permissionIds: originalRole.permissions.map(
					permission => permission.id,
				),
				baseRoleId: originalRole.baseRoleId,
			};
			return editingRole;
		},
		[UserGetterKeys.userPermissions]: state => state.userPermissions || [],
		[UserGetterKeys.userPermissionItems]: (_, getters) => {
			const allPermissions = getters[
				UserGetterKeys.userPermissions
			] as UserPermission[];
			return allPermissions
				.map(permission => permission.item)
				.sort((iA, iB) => iA.index - iB.index)
				.reduce((items, item) => {
					if (!items.find(i => i.id === item.id))
						items.push({
							...item,
							permissions: allPermissions.filter(p => p.item.id === item.id),
						});
					return items;
				}, [] as UserPermissionItem[]);
		},
		[UserGetterKeys.userPermissionItemGroups]: (_, getters) => {
			const allItems = getters[
				UserGetterKeys.userPermissionItems
			] as UserPermissionItem[];
			return allItems
				.map(permissionItem => permissionItem.group)
				.sort((gA, gB) => gA.index - gB.index)
				.reduce((groups, group) => {
					if (!groups.find(g => g.id === group.id))
						groups.push({
							items: allItems.filter(i => i.group.id === group.id),
							...group,
						});
					return groups;
				}, [] as UserPermissionItemGroup[]);
		},
		[UserGetterKeys.permissionsEditing]: state => state.permissionsEditing,
	},
};

export default store;
